import React, { useState, useEffect, useRef } from 'react';
import { format, startOfWeek, addDays, subDays, isToday } from 'date-fns';
import { es } from 'date-fns/locale';
import { getWeekMealPlan, createMealPlanItem, deleteMealPlanItem } from '../api';
import RecipeSelector from './RecipeSelector';
import { useTheme } from '../context/ThemeContext';
import './WeekView.css';

const WeekView = () => {
  const [centerDate, setCenterDate] = useState(new Date());
  const [mealPlans, setMealPlans] = useState({});
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedMealType, setSelectedMealType] = useState(null);
  const [showRecipeSelector, setShowRecipeSelector] = useState(false);
  const [expandedRecipe, setExpandedRecipe] = useState(null);
  const { theme } = useTheme();
  const todayRef = useRef(null);

  const visibleStartDay = subDays(centerDate, 2);
  const weekDays = Array.from({ length: 5 }, (_, i) => addDays(visibleStartDay, i));

  useEffect(() => {
    loadWeekMealPlan();
  }, [centerDate]);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= 768 && todayRef.current) {
      todayRef.current.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'center' });
    }
  }, []);

  const loadWeekMealPlan = async () => {
    try {
      const visibleStart = subDays(centerDate, 2);
      const visibleEnd = addDays(centerDate, 2);
      const weekStartKeys = [
        format(startOfWeek(visibleStart, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        format(startOfWeek(visibleEnd, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
      ];

      const uniqueWeekStarts = Array.from(new Set(weekStartKeys));
      const grouped = {};

      for (const weekStartKey of uniqueWeekStarts) {
        const response = await getWeekMealPlan(weekStartKey);
        const items = response.data;

        items.forEach(item => {
          const dateKey = item.date;
          if (!grouped[dateKey]) {
            grouped[dateKey] = { desayuno: [], almuerzo: [], comida: [], cena: [] };
          }
          if (!grouped[dateKey][item.meal_type]) {
            grouped[dateKey][item.meal_type] = [];
          }
          grouped[dateKey][item.meal_type].push(item);
        });
      }

      setMealPlans(grouped);
    } catch (error) {
      console.error('Error cargando plan semanal:', error);
    }
  };

  const handlePreviousWeek = () => {
    setCenterDate(subDays(centerDate, 5));
  };

  const handleNextWeek = () => {
    setCenterDate(addDays(centerDate, 5));
  };

  const handleAddMeal = (day, mealType) => {
    setSelectedDay(day);
    setSelectedMealType(mealType);
    setShowRecipeSelector(true);
  };

  const handleRecipeSelected = async (recipeId) => {
    try {
      await createMealPlanItem({
        date: format(selectedDay, 'yyyy-MM-dd'),
        meal_type: selectedMealType,
        recipe_id: recipeId,
      });
      setShowRecipeSelector(false);
      loadWeekMealPlan();
    } catch (error) {
      console.error('Error agregando comida:', error);
    }
  };

  const handleRemoveMeal = async (itemId) => {
    try {
      await deleteMealPlanItem(itemId);
      loadWeekMealPlan();
    } catch (error) {
      console.error('Error eliminando comida:', error);
    }
  };

  const getMealIcon = (mealType) => {
    const icons = {
      comida: '🍽️',
      cena: '🌙',
      desayuno: '☕',
      almuerzo: '🥗',
    };
    return icons[mealType] || '🍴';
  };

  const handleRecipeClick = (itemId) => {
    if (expandedRecipe === itemId) {
      setExpandedRecipe(null);
    } else {
      setExpandedRecipe(itemId);
    }
  };

  const getCaloriesColor = (calories) => {
    if (!calories) return null;
    if (calories < 300) return 'green';
    if (calories <= 700) return 'yellow';
    return 'red';
  };

  const parseIngredients = (ingredients) => {
    if (!ingredients) return [];
    return ingredients.split(';').map(ing => ing.trim()).filter(ing => ing.length > 0);
  };

  const getDayCalories = (dayMeals) => {
    return ['desayuno', 'almuerzo', 'comida', 'cena'].reduce((total, type) => {
      const meals = dayMeals[type] || [];
      return total + meals.reduce((sum, item) => sum + (item.recipe.calories || 0), 0);
    }, 0);
  };

  return (
    <div className={`week-view ${theme}-theme`}>
      <div className="week-navigation">
        <button onClick={handlePreviousWeek} className="btn-nav">←</button>
        <div className="week-info">
          <p className="month-name">{format(centerDate, 'MMMM', { locale: es })}</p>
        </div>
        <button onClick={handleNextWeek} className="btn-nav">→</button>
      </div>

      <div className="week-grid">
        {weekDays.map((day) => {
          const dateKey = format(day, 'yyyy-MM-dd');
          const dayMeals = mealPlans[dateKey] || { desayuno: [], almuerzo: [], comida: [], cena: [] };
          const totalCalories = getDayCalories(dayMeals);
          
          const isTodayDay = isToday(day);
          
          return (
            <div
              key={dateKey}
              className={`day-card ${isTodayDay ? 'today' : ''}`}
              ref={isTodayDay ? todayRef : null}
            >
              <div className="day-header">
                <h3>{format(day, 'EEEE', { locale: es })}</h3>
                {totalCalories ? (
                  <div className="day-calories">
                    {`${totalCalories} kcal`}
                  </div>
                ) : (
                  <div className="day-calories empty" />
                )}
                <span className="day-number">{format(day, 'd')}</span>
              </div>
              
              {['desayuno', 'almuerzo', 'comida', 'cena'].map((mealType) => (
                <div key={mealType} className="meal-slot">
                  <div className="meal-type-label">
                    {mealType.charAt(0).toUpperCase() + mealType.slice(1)}
                  </div>
                  <div className="meal-items">
                    {dayMeals[mealType] && dayMeals[mealType].length > 0 &&
                      dayMeals[mealType].map((item) => {
                        const isExpanded = expandedRecipe === item.id;
                        const ingredients = parseIngredients(item.recipe.ingredients);
                        const caloriesColor = getCaloriesColor(item.recipe.calories);
                        
                        return (
                          <div key={item.id} className={`meal-item ${isExpanded ? 'expanded' : ''}`}>
                            <div 
                              className="meal-item-header"
                              onClick={() => handleRecipeClick(item.id)}
                            >
                              <span className="meal-icon">{getMealIcon(mealType)}</span>
                              <span className="meal-name">{item.recipe.title}</span>
                              {item.recipe.calories && (
                                <span className={`calories-badge ${caloriesColor}`}>
                                  {item.recipe.calories} kcal
                                </span>
                              )}
                              <button
                                className="btn-remove"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveMeal(item.id);
                                }}
                              >
                                ✕
                              </button>
                            </div>
                            {isExpanded && (
                              <div className="meal-item-details">
                                <div className="ingredients-list">
                                  <strong>Ingredientes:</strong>
                                  <ul>
                                    {ingredients.map((ing, idx) => (
                                      <li key={idx}>{ing}</li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    {(!dayMeals[mealType] || dayMeals[mealType].length === 0) && (
                      <button
                        className="btn-add"
                        onClick={() => handleAddMeal(day, mealType)}
                      >
                        + Agregar
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {showRecipeSelector && (
        <RecipeSelector
          onSelect={handleRecipeSelected}
          onClose={() => setShowRecipeSelector(false)}
          selectedDate={selectedDay}
          mealType={selectedMealType}
          theme={theme}
        />
      )}
    </div>
  );
};

export default WeekView;
